import { useState, useEffect } from 'react'
import { X, Send, Minimize2, Maximize2 } from 'lucide-react'
import { mockAccounts } from '../../data/mockEmails'

interface ComposeModalProps {
  onClose: () => void
}

export default function ComposeModal({ onClose }: ComposeModalProps) {
  const [isMinimized, setIsMinimized] = useState(false)
  const [fromAccountId, setFromAccountId] = useState(mockAccounts[0]?.id || '')
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [isVisible, setIsVisible] = useState(false)

  // Trigger entrance animation on mount
  useEffect(() => {
    // Small delay to ensure the initial state is rendered first
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    // Wait for animation to complete before calling onClose
    setTimeout(onClose, 300)
  }

  const handleSend = () => {
    // In the future, this will send via the selected account's API
    console.log('Sending email:', { fromAccountId, to, subject, body })
    handleClose()
  }

  // Minimized state - only show on desktop
  if (isMinimized) {
    return (
      <div className="fixed bottom-0 right-6 w-72 bg-white rounded-t-lg shadow-xl border border-gray-200 hidden lg:block">
        <div className="flex items-center justify-between p-3 bg-gray-800 text-white rounded-t-lg cursor-pointer">
          <span className="font-medium truncate">
            {subject || 'New Message'}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(false)}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <Maximize2 size={14} />
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Mobile backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Compose Modal */}
      <div
        className={`
          fixed z-50 bg-white flex flex-col

          /* Mobile: Full screen with slide-up animation */
          inset-0
          transform transition-transform duration-300 ease-out
          ${isVisible ? 'translate-y-0' : 'translate-y-full'}

          /* Desktop: Bottom-right floating panel */
          lg:inset-auto lg:bottom-0 lg:right-6 lg:w-[560px] lg:max-h-[600px]
          lg:rounded-t-lg lg:shadow-xl lg:border lg:border-gray-200
          lg:translate-y-0
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-3 bg-gray-800 text-white lg:rounded-t-lg shrink-0">
          <span className="font-medium text-lg lg:text-base">New Message</span>
          <div className="flex items-center gap-2 lg:gap-1">
            {/* Minimize button - desktop only */}
            <button
              onClick={() => setIsMinimized(true)}
              className="hidden lg:block p-1 hover:bg-gray-700 rounded"
            >
              <Minimize2 size={16} />
            </button>
            <button
              onClick={handleClose}
              className="p-2 lg:p-1 hover:bg-gray-700 rounded active:bg-gray-600"
            >
              <X size={20} className="lg:hidden" />
              <X size={16} className="hidden lg:block" />
            </button>
          </div>
        </div>

        {/* Form - scrollable on mobile */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {/* From */}
          <div className="flex items-center border-b border-gray-200 px-4 py-3 lg:py-2">
            <label className="text-sm text-gray-500 w-20 lg:w-16">From:</label>
            <select
              value={fromAccountId}
              onChange={(e) => setFromAccountId(e.target.value)}
              className="flex-1 text-base lg:text-sm outline-none bg-transparent min-h-[44px] lg:min-h-0"
            >
              {mockAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.email}
                </option>
              ))}
            </select>
          </div>

          {/* To */}
          <div className="flex items-center border-b border-gray-200 px-4 py-3 lg:py-2">
            <label className="text-sm text-gray-500 w-20 lg:w-16">To:</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="flex-1 text-base lg:text-sm outline-none min-h-[44px] lg:min-h-0"
              placeholder="recipient@email.com"
              autoComplete="email"
            />
          </div>

          {/* Subject */}
          <div className="flex items-center border-b border-gray-200 px-4 py-3 lg:py-2">
            <label className="text-sm text-gray-500 w-20 lg:w-16">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="flex-1 text-base lg:text-sm outline-none min-h-[44px] lg:min-h-0"
              placeholder="Email subject"
            />
          </div>

          {/* Body */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="flex-1 p-4 text-base lg:text-sm outline-none resize-none min-h-[200px] lg:min-h-[200px]"
            placeholder="Write your message..."
          />
        </div>

        {/* Footer - fixed at bottom on mobile */}
        <div className="p-4 lg:p-3 border-t border-gray-200 flex justify-between items-center shrink-0 bg-white">
          <button
            onClick={handleSend}
            disabled={!to || !subject}
            className="flex items-center gap-2 px-6 lg:px-4 py-3 lg:py-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors min-h-[48px] lg:min-h-0"
          >
            <Send size={18} className="lg:hidden" />
            <Send size={16} className="hidden lg:block" />
            Send
          </button>
          <button
            onClick={handleClose}
            className="px-6 lg:px-4 py-3 lg:py-2 text-gray-600 hover:text-gray-800 active:text-gray-900 transition-colors min-h-[48px] lg:min-h-0"
          >
            Discard
          </button>
        </div>
      </div>
    </>
  )
}
